/**
 * 应用入口文件
 * 负责：登录校验、用户信息加载、主题/语言管理、路由配置
 */
import './style/global.less';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { ConfigProvider } from '@arco-design/web-react';
import zhCN from '@arco-design/web-react/es/locale/zh-CN';
import enUS from '@arco-design/web-react/es/locale/en-US';
import { BrowserRouter, Switch, Route } from 'react-router-dom';
import PageLayout from './layout';
import { GlobalContext } from './context';
import Login from './pages/login';
import changeTheme from './utils/changeTheme';
import useStorage from './utils/useStorage';
import './mock';
import { getUserInfoApi, getEditCourseListIds } from '@/request/api';
import { logger } from '@/utils/logger';
import KouziChatView from './pages/eduQuads/educhart';
import { useAppStore } from './store/appStore';
import ForceChangePasswordModal from './components/ForceChangePasswordModal';

function Index() {
  // 语言和主题偏好（持久化到 localStorage）
  const [lang, setLang] = useStorage('arco-lang', 'zh-CN');
  const [theme, setTheme] = useStorage('arco-theme', 'light');

  // 强制修改密码弹窗状态
  const [forceChangePasswordVisible, setForceChangePasswordVisible] = useState(false);

  // Zustand store actions
  const { setUserInfo, setUserLoading } = useAppStore((state) => ({
    setUserInfo: state.setUserInfo,
    setUserLoading: state.setUserLoading,
  }));

  // Arco Design 国际化配置
  const arcoLocale = React.useMemo(() => (lang === 'en-US' ? enUS : zhCN), [lang]);

  // 获取用户信息并初始化权限
  const fetchUserInfo = React.useCallback(() => {
    setUserLoading(true);

    getUserInfoApi()
      .then((res) => {
        const { data } = res;
        localStorage.setItem('userInfo', JSON.stringify(res.data));
        const userRole = data.permission?.roleCodeList?.[0] || '';
        localStorage.setItem('userRole', userRole);
        localStorage.setItem('sysRoleId', data.sysRoleId || '');

        // 检查密码是否过于简单，需要强制修改
        if (data.easyPassword === 1) {
          setForceChangePasswordVisible(true);
        }

        // 异步获取可编辑课程列表，不阻塞主流程
        getEditCourseListIds()
          .then((resEditCourse) => {
            localStorage.setItem(
              'editCourseListIds',
              JSON.stringify({ data: resEditCourse.data })
            );
          })
          .catch((error) => {
            logger.error('获取可编辑课程列表失败:', error);
          });

        // 所有登录用户拥有全部权限，不进行权限过滤
        const permissions: Record<string, string[]> = { '*': ['*'] };

        logger.log('userInfo data:', data);
        setUserInfo(
          {
            ...data,
            permissions,
          },
          false
        );
      })
      .catch((error) => {
        logger.error('获取用户信息失败:', error);
        setUserLoading(false);
      });
  }, [setUserInfo, setUserLoading]);

  // 登录校验：已登录则加载用户信息，未登录则跳转登录页
  useEffect(() => {
    const isLoggedIn = localStorage.getItem('userStatus') === 'login';
    const isLoginPage = window.location.pathname === '/login';

    if (isLoggedIn) {
      fetchUserInfo();
    } else if (!isLoginPage) {
      window.location.pathname = '/login';
    }
  }, [fetchUserInfo]);

  // 主题切换
  useEffect(() => {
    changeTheme(theme);
  }, [theme]);

  // 全局上下文：语言和主题管理
  const contextValue = React.useMemo(
    () => ({
      lang,
      setLang,
      theme,
      setTheme,
    }),
    [lang, theme]
  );

  return (
    <BrowserRouter>
      <ConfigProvider
        locale={arcoLocale}
        componentConfig={{
          Card: { bordered: false },
          List: { bordered: false },
          Table: { border: false },
        }}
      >
        <GlobalContext.Provider value={contextValue}>
          <Switch>
            <Route path="/login" component={Login} />
            <Route path="/eduQuads/educhart" component={KouziChatView} />
            <Route path="/" component={PageLayout} />
          </Switch>
          <ForceChangePasswordModal visible={forceChangePasswordVisible} />
        </GlobalContext.Provider>
      </ConfigProvider>
    </BrowserRouter>
  );
}

// 挂载应用
const root = ReactDOM.createRoot(document.getElementById('root') as HTMLElement);
root.render(<Index />);
